/// <amd-module name="Wiki/PublishCodeWikiPanel/PublishCodeWikiPanel" />
import * as React from "react";
import { ITfsPageService } from "Tfs/Platform/Page";

import { RepositoryDropdown } from "Repos/RepoDropdown/Components/RepositoryDropdown";
import { IRepositoryDropdownItem } from "Repos/RepoDropdown/RepositoryDropdownProvider";

import { RepositoryDropdownComponent } from "VC/Common/Components/RepositoryDropdown";
import { VersionDropdown } from "VC/Common/Components/VersionDropdown";
import { GitRepository } from "VC/Common/Contracts/Git";
import { IDeprecatedGitRepositoryService } from "VC/Common/GitRepositoryService";
import { getFriendlyName, getVersionFromBranchName } from "VC/Common/Utils/Ref";
import { IVssComponentContext, IVssLayoutManager, VssComponent } from "VSS/Platform/Layout";
import { IVssPageService } from "VSS/Platform/Page";
import { Button, IButtonProps } from "VSSUI/Button";
import { ContentSize } from "VSSUI/Callout";
import { FormItem } from "VSSUI/FormItem";
import { MessageCard, MessageCardSeverity } from "VSSUI/MessageCard";
import { Panel } from "VSSUI/Panel";
import { Spinner } from "VSSUI/Spinner";
import { TextFieldWithMessage } from "VSSUI/TextField";
import { GitVersionOptions, GitVersionType } from "Wiki/Clients/VC/Contracts/Git";
import { WikiType, WikiV2 } from "Wiki/Clients/Wiki/Contracts/Wiki";
import { IWikiRestClient } from "Wiki/Clients/Wiki/RestClient/Wiki";
import { FolderSelectionDialog } from "Wiki/PublishCodeWikiPanel/FolderSelectionDialog";
import * as Resources from "Wiki/PublishCodeWikiPanel/Resources";
import { RepoConstants } from "Wiki/ViewCommon/Constants";
import { TelemetryConstants } from "Wiki/ViewCommon/CustomerIntelligenceConstants";
import { validateProjectName } from "Wiki/ViewCommon/Utils/ProjectNameValidator";
import { navigateToUrl, publish } from "Wiki/ViewCommon/Utils/WikiHelper";
import { IListBoxItem } from "VSSUI/ListBox";

export interface IPublishCodeWikiProps {
    onDismiss: () => void;
}

export interface IPublishCodeWikiState {
    isLoadingData: boolean;
    isPublishingWiki: boolean;
    repositoryDropdownItem?: IRepositoryDropdownItem;
    version: string;
    folderPath: string;
    folderPathError?: string;
    wikiName: string;
    wikiNameError?: string;
    dataLoadError?: string;
    dataPublishError?: string;
}

export class PublishCodeWikiPanel extends VssComponent<IPublishCodeWikiProps, IPublishCodeWikiState> {
    private _getAllRepositoriesPromise: Promise<GitRepository[]>;

    constructor(props: IPublishCodeWikiProps, context: IVssComponentContext) {
        super(props, context);
        this.state = {
            folderPath: "",
            wikiName: "",
            version: "",
            isLoadingData: true,
            isPublishingWiki: false
        };
    }

    public componentDidMount() {
        super.componentDidMount();
        this._loadInitialData();
    }

    public render(): JSX.Element {
        const showSpinner = this.state.isLoadingData || this.state.isPublishingWiki;

        return (
            <Panel
                onDismiss={this.props.onDismiss}
                titleProps={{ text: Resources.PublishWikiPanelTitle }}
                description={Resources.PublishWikiPanelDescription}
                footerButtonProps={this._getFooterButtonProps()}
                size={ContentSize.Medium}
            >
                {showSpinner ? (
                    <Spinner
                        className="pcwp-spinner"
                        label={this.state.isLoadingData ? Resources.LoadingSpinnerText : Resources.PublishingSpinnerText}
                    />
                ) : this.state.dataLoadError ? (
                    <MessageCard severity={MessageCardSeverity.Error}>{this.state.dataLoadError}</MessageCard>
                ) : (
                    <div className="pcwp-content">
                        <FormItem label={Resources.RepositoryLabel}>{
                            <RepositoryDropdown
                                className="pcwp-repo-picker repository-dropdown-codewiki"
                                onSelect={this.onRepositoryChanged}
                                selectedRepositoryItem={this.getSelectedRepositoryItem()}
                                providerOptions={{
                                    projectId: this._getProjectId(),
                                    supportsTfvc: false,
                                }}
                                useDefaultCalloutWidth={false}
                            />
                        }
                        </FormItem>
                        <FormItem label={Resources.BranchLabel}>
                            <VersionDropdown
                                providerOptions={{
                                    repository: this.state.repositoryDropdownItem && this.state.repositoryDropdownItem.repository,
                                    currentIdentityId: this._getUserId(),
                                    viewMyBranches: true,
                                    viewTagsPivot: false,
                                    viewCommitsPivot: false,
                                    selectedVersionString: this.state.version,
                                    onVersionSelected: this._onVersionChange
                                }}
                                placeholderText={Resources.VersionSelectorPlaceholderText}
                            />
                        </FormItem>
                        <FormItem label={Resources.FolderLabel}>
                            <div className="flex-row">
                                <TextFieldWithMessage
                                    message={this.state.folderPathError}
                                    error={Boolean(this.state.folderPathError)}
                                    textFieldProps={{
                                        placeholder: Resources.FolderFieldPlaceHolder,
                                        value: this.state.folderPath,
                                        onChange: this._onFolderPathChange
                                    }}
                                    className="flex-grow"
                                />
                                <Button
                                    text="..."
                                    onClick={this._onFolderButtonClick}
                                    className="pcpw pcpw-select-folder"
                                    ariaLabel={Resources.FolderPickerButtonLabel}
                                />
                            </div>
                        </FormItem>
                        <FormItem label={Resources.WikiNameLabel}>
                            <TextFieldWithMessage
                                message={this.state.wikiNameError}
                                error={Boolean(this.state.wikiNameError)}
                                textFieldProps={{
                                    placeholder: Resources.WikiNameFieldPlaceHolder,
                                    value: this.state.wikiName,
                                    onChange: this._onWikiNameChange
                                }}
                            />
                        </FormItem>
                        {this.state.dataPublishError && <FormItem message={this.state.dataPublishError} error={true} />}
                    </div>
                )}
            </Panel>
        );
    }

    private onRepositoryChanged = (event: React.SyntheticEvent<HTMLElement>, item: IListBoxItem<IRepositoryDropdownItem>) => {
        const currentRepoId = this.state.repositoryDropdownItem && this.state.repositoryDropdownItem.id;
        const repositoryDropdownItem = item.data;

        if (repositoryDropdownItem && repositoryDropdownItem.id !== currentRepoId) {
            this.setState({
                repositoryDropdownItem,
                version: this._getDefaultVersion(repositoryDropdownItem.repository),
                folderPath: "",
                folderPathError: undefined
            });
        }
    };

    private getSelectedRepositoryItem = (): IRepositoryDropdownItem | undefined => {
        const repositoryItem = this.state.repositoryDropdownItem;

        if (repositoryItem) {
            return {
                id: repositoryItem.id,
                name: repositoryItem.name ? repositoryItem.name : "$/" + repositoryItem.repository?.project.name,
                isTfvc: !!repositoryItem.isTfvc,
                isFork: !!repositoryItem.isFork
            };
        }
        return undefined;
    };

    private _onFolderButtonClick = (): void => {
        let repoItem: IRepositoryDropdownItem;

        repoItem = this.state.repositoryDropdownItem!;
        const layoutManager = this.context.pageContext.getService<IVssLayoutManager>("IVssLayoutManager");
        layoutManager.renderCallout(onDismiss => {
            return (
                <FolderSelectionDialog
                    repository={repoItem.repository!}
                    friendlyBranchName={_getFriendlyBranchNameFromVersion(this.state.version)}
                    initialPagePath={this.state.folderPath}
                    onSelected={(path: string) => {
                        this._onFolderPathChange(undefined, path);
                        onDismiss();
                    }}
                    onDismiss={onDismiss}
                />
            );
        });
    };

    private _onVersionChange = (version: string): void => {
        this.setState({ version });
    };

    private _onFolderPathChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | undefined, path: string): void => {
        this.setState({ folderPath: path, folderPathError: this._isFolderPathValid(path) ? undefined : Resources.InvalidFolderPathError });
    };

    private _onWikiNameChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, name: string): void => {
        this.setState({ wikiName: name, wikiNameError: validateProjectName(name).invalidNameReason });
    };

    private _isRequiredInfoComplete = (): boolean => {
        let repoItem: IRepositoryDropdownItem;
        repoItem = this.state.repositoryDropdownItem!;

        return (
            Boolean(repoItem) &&
            Boolean(this.state.version) &&
            this._isFolderPathValid(this.state.folderPath) &&
            validateProjectName(this.state.wikiName).isValid
        );
    };

    private _getDefaultVersion = (repository?: GitRepository): string => {
        if (repository && repository.defaultBranch) {
            return getVersionFromBranchName(getFriendlyName(repository.defaultBranch));
        }

        return "";
    };

    private _loadInitialData = (): void => {
        const repositoryService = this.context.pageContext.getService<IDeprecatedGitRepositoryService>("IDeprecatedGitRepositoryService");
        this._getAllRepositoriesPromise = repositoryService.getAllRepositories(this._getProjectId());
        this.trackPromise(this._getAllRepositoriesPromise).promise.then(
            repositories => {
                const repo = repositories[0];
                this.setState({
                    isLoadingData: false,
                    repositoryDropdownItem: {
                        name: repo.name,
                        isTfvc: false,
                        isFork: repo.isFork,
                        id: repo.id,
                        repository: repo
                    },
                    version: this._getDefaultVersion(repo)
                });
            },
            error => {
                this.setState({
                    dataLoadError: error.message,
                    isLoadingData: false
                });
            }
        );
    };

    private _onPublish = (): void => {
        const wikiClient = this.context.pageContext.getRestClient<IWikiRestClient>("IWikiRestClient");
        this.trackPromise(
            wikiClient.createWiki({
                name: this.state.wikiName,
                type: WikiType.CodeWiki,
                mappedPath: this.state.folderPath,
                projectId: this._getProjectId(),
                repositoryId: this.state.repositoryDropdownItem ? this.state.repositoryDropdownItem.id : "",
                version: {
                    versionOptions: GitVersionOptions.None,
                    versionType: GitVersionType.Branch,
                    version: _getFriendlyBranchNameFromVersion(this.state.version)
                }
            })
        ).promise.then(
            (wiki: WikiV2) => {
                publish(this.context.pageContext, TelemetryConstants.WikiCreated, TelemetryConstants.WikiTypeCode);
                navigateToUrl(this.context.pageContext, wiki.projectId, wiki.name);
            },
            (error: Error) => {
                this.setState({ isPublishingWiki: false, dataPublishError: error.message });
            }
        );

        this.setState({ isPublishingWiki: true });
    };

    private _isFolderPathValid = (path: string): boolean => {
        return Boolean(path) && path.startsWith(RepoConstants.RootPath);
    };

    private _getFooterButtonProps = (): IButtonProps[] => {
        return [
            { text: Resources.Cancel, onClick: this.props.onDismiss, disabled: this.state.isPublishingWiki },
            {
                text: Resources.Publish,
                primary: true,
                onClick: this._onPublish,
                disabled: !this._isRequiredInfoComplete() || this.state.isPublishingWiki || Boolean(this.state.dataLoadError)
            }
        ];
    };

    /**
     * This action is supported at project level. So projectId should always be present.
     */
    private _getProjectId = (): string => {
        const tfsPageData = this.context.pageContext.getService<ITfsPageService>("ITfsPageService").getData();
        return tfsPageData && tfsPageData.project ? tfsPageData.project.id : "";
    };

    private _getUserId = (): string => {
        const vssPageData = this.context.pageContext.getService<IVssPageService>("IVssPageService").getData();
        return vssPageData ? vssPageData.user.id : "";
    };
}

/**
 *  Convert version to branchName by removing prefix "GB"
 */
function _getFriendlyBranchNameFromVersion(version: string): string {
    return version.substring(2);
}

VssComponent.register("Wiki.PublishCodeWikiPanel", PublishCodeWikiPanel);
